import type { ChainId } from '../web3.types'

/**
 * Parses an `ANVIL_RPC_URLS` value of shape `"<chainId>|<url>[,<chainId>|<url>]*"`
 * into a `Map<ChainId, string>`. Whitespace around entries and around `|` is
 * trimmed; empty entries are skipped; malformed entries are skipped with a
 * `console.warn`.
 *
 * URLs must not contain `,` or `|` — anvil fork URLs (`http://localhost:PORT`)
 * are safe.
 * @internal
 */
export function parseAnvilRpcUrls(value: string | undefined): Map<ChainId, string> {
  const map = new Map<ChainId, string>()
  if (!value) return map

  for (const entry of value.split(',')) {
    const trimmed = entry.trim()
    if (!trimmed) continue

    const pipe = trimmed.indexOf('|')
    if (pipe === -1) {
      console.warn(`ANVIL_RPC_URLS: skipping malformed entry "${trimmed}" (no '|')`)
      continue
    }

    const chainIdStr = trimmed.slice(0, pipe).trim()
    const url = trimmed.slice(pipe + 1).trim()
    const chainId = Number(chainIdStr)
    if (!Number.isFinite(chainId) || chainIdStr === '' || !url) {
      console.warn(`ANVIL_RPC_URLS: skipping malformed entry "${trimmed}"`)
      continue
    }

    map.set(chainId as ChainId, url)
  }

  return map
}

/**
 * Measured against anvil 1.8.1: 32 KiB of `eth_call` calldata executes, and 64 KiB is refused for
 * exceeding EIP-3860's initcode limit — a bound the chain already states as `viemDlc
 * .maxInitcodeSize`, not a request cap. Anvil documents no request cap of its own, so this is
 * headroom against the largest chunk a fork has been seen to take, not a limit it enforces.
 */
export const ANVIL_MAX_CALLDATA_BYTES = 32_768

/**
 * Returns the anvil RPC URL for `chainId` if one is configured via
 * `ANVIL_RPC_URLS`, else `undefined`. Feed this into `rpcs.anvil`, which carries anvil's own
 * limits; the E2E branch of a server client's candidate list typically looks like:
 *
 * ```ts
 * rpcs.anvil.chain(chainId).with({ url: getAnvilRpcUrl(chainId, getServerEnvVar) })
 * ```
 */
export function getAnvilRpcUrl(
  chainId: ChainId,
  getEnvVar: (name: 'ANVIL_RPC_URLS') => string | undefined
): string | undefined {
  return parseAnvilRpcUrls(getEnvVar('ANVIL_RPC_URLS')).get(chainId)
}
